import { View, ImageSourcePropType, StyleSheet, Platform, useWindowDimensions } from 'react-native';
import Svg, { Pattern, Rect, Defs, SvgFromUri, SvgXml } from 'react-native-svg';
import { Asset } from 'expo-asset';
import * as FileSystem from 'expo-file-system/legacy';
import { useState, useEffect, useMemo, useId, memo } from 'react';

// Cache for SVG dimensions and content to prevent flickering on navigation
const svgCache = new Map<string, { width: number; height: number; xml: string }>();

type ImagePatternProps = {
  source?: number | ImageSourcePropType;
  backgroundColor?: string;
  opacity?: number;
  scale?: number;
};

const calculateBrightness = (color: string): number => {
  // Handle hex colors
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 2), 16);
    const b = parseInt(hex.slice(4, 2), 16);
    return (r * 299 + g * 587 + b * 114) / 1000;
  }
  // Handle rgb/rgba colors
  const rgb = color.match(/\d+/g);
  if (rgb && rgb.length >= 3) {
    const [r, g, b] = rgb.map(Number);
    return (r * 299 + g * 587 + b * 114) / 1000;
  }
  return 128; // Default middle brightness
};

// Robustly extract dimensions from SVG content
const getSvgDimensions = (content: string) => {
  // Try width/height attributes first
  const widthMatch = content.match(/width="([^"]+)"/);
  const heightMatch = content.match(/height="([^"]+)"/);
  
  let width = widthMatch ? parseFloat(widthMatch[1]) : 0;
  let height = heightMatch ? parseFloat(heightMatch[1]) : 0;

  // Fallback to viewBox if width/height are missing or 0
  if (!width || !height) {
    const viewBoxMatch = content.match(/viewBox="([^"]+)"/);
    if (viewBoxMatch) {
      const parts = viewBoxMatch[1].trim().split(/[\s,]+/);
      if (parts.length === 4) {
        width = parseFloat(parts[2]);
        height = parseFloat(parts[3]);
      }
    }
  }

  // Final fallback defaults
  return {
    width: width || 100,
    height: height || 100
  };
};

// Simple, safe hash function for generating stable IDs
const simpleHash = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
};

function ImagePatternComponent({
  source,
  backgroundColor = '#808080',
  opacity: opacityOverride,
  scale = 1,
}: ImagePatternProps) {
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const [dimensions, setDimensions] = useState({ width: windowWidth, height: windowHeight });

  useEffect(() => {
    if (windowWidth === 0 || windowHeight === 0) return;

    // Debounce window resize to prevent excessive re-renders on web
    // On native, a small delay ensures we get the final dimensions after rotation
    const delay = Platform.OS === 'web' ? 200 : 50;
    
    const handler = setTimeout(() => {
      setDimensions({ width: windowWidth, height: windowHeight });
    }, delay);

    return () => clearTimeout(handler);
  }, [windowWidth, windowHeight]);

  // Memoize opacity calculation
  const opacity = useMemo(() => {
    if (opacityOverride !== undefined) return opacityOverride;
    const brightness = calculateBrightness(backgroundColor);
    return 0.15 + 0.3 * (brightness / 255);
  }, [backgroundColor, opacityOverride]);
  
  // Resolve asset URI
  let uri: string | undefined;
  let assetType: string | undefined;

  if (typeof source === 'number') {
    // Asset.fromModule is synchronous for local assets
    const asset = Asset.fromModule(source);
    uri = asset.uri;
    assetType = asset.type;
  } else if (typeof source === 'object' && source !== null && 'uri' in source) {
    uri = (source as { uri: string }).uri;
  }

  // Robust SVG detection
  // If source is a number (local asset), we trust it might be an SVG even if the URI doesn't have the extension in production
  // But checking type is safer if available.
  const isSvg = assetType === 'svg' || 
                uri?.toLowerCase().includes('.svg') || 
                uri?.toLowerCase().startsWith('data:image/svg') ||
                // Fallback: if it's a local asset (number) and we don't have type/ext info, assume it might be SVG if user passed it to ImagePattern
                (typeof source === 'number' && !assetType);

  const [svgData, setSvgData] = useState<{ width: number; height: number; xml: string | null }>(() => {
    if (isSvg && uri && svgCache.has(uri)) {
      return svgCache.get(uri)!;
    }
    return { width: 0, height: 0, xml: null };
  });
  
  const [isLoading, setIsLoading] = useState(() => {
    if (!isSvg || !uri) return false; 
    return !svgCache.has(uri);
  });

  // Stable per instance, unique per header: on web SVG ids are document-wide, so a shared id makes a
  // screen's header resolve to the pattern inside a hidden stacked screen and render a flat color
  const instanceId = useId();
  const patternId = useMemo(() => {
    const keyString = `${source}-${scale}`;
    return `pattern-${simpleHash(keyString)}-${instanceId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  }, [source, scale, instanceId]);

  useEffect(() => {
    if (!isSvg) {
      setIsLoading(false);
      return;
    }

    if (!uri) return;

    // Check cache again in effect
    if (svgCache.has(uri)) {
      const cached = svgCache.get(uri)!;
      setSvgData(prev => (prev.xml === cached.xml ? prev : cached));
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    let isMounted = true;

    const loadSvgContent = async () => {
      try {
        let content = '';
        
        if (typeof source === 'number') {
           // Local asset (require)
           const asset = Asset.fromModule(source);
           await asset.downloadAsync();
           if (asset.localUri) {
             content = await FileSystem.readAsStringAsync(asset.localUri);
           } else {
             // Fallback for web or if localUri is missing (e.g. web dev)
             const res = await fetch(asset.uri);
             content = await res.text();
           }
        } else {
           // Remote or other URI
           if (Platform.OS !== 'web' && (uri.startsWith('file://') || uri.startsWith('content://'))) {
              content = await FileSystem.readAsStringAsync(uri);
           } else {
              const res = await fetch(uri);
              content = await res.text();
           }
        }

        if (!isMounted) return;

        const dims = getSvgDimensions(content);
        const newData = { ...dims, xml: content };
        
        if (uri) svgCache.set(uri, newData);
        setSvgData(newData);
        setIsLoading(false);
      } catch (err) {
        console.warn('Failed to load SVG pattern', err);
        if (!isMounted) return;
        const fallbackData = { width: 100, height: 100, xml: null };
        setSvgData(fallbackData);
        setIsLoading(false);
      }
    };

    loadSvgContent();

    return () => {
      isMounted = false;
    };
  }, [source, isSvg, uri]);

  if (!isSvg) return null;

  // 2. Handle SVGs
  if (isLoading || svgData.width === 0 || !svgData.xml) {
    return null;
  }

  return (
    <View
      key={`svg-pattern-${dimensions.width}-${dimensions.height}`}
      style={[
        StyleSheet.absoluteFill,
        { opacity, overflow: 'hidden' }
      ]}
    >
      <Svg width="100%" height="100%">
        <Defs>
          <Pattern
            id={patternId}
            patternUnits="userSpaceOnUse"
            x="0"
            y="0"
            width={svgData.width * scale}
            height={svgData.height * scale}
            viewBox={`0 0 ${svgData.width} ${svgData.height}`}
          >
            <SvgXml 
              xml={svgData.xml} 
              width={svgData.width} 
              height={svgData.height} 
            />
          </Pattern>
        </Defs>
        <Rect 
          width="100%" 
          height="100%" 
          fill={`url(#${patternId})`} 
        />
      </Svg>
    </View>
  );
}

export const ImagePattern = memo(ImagePatternComponent);
